/**
 * Server frames of a knowledge-base training session on the duplex transport.
 *
 * The server decides everything: which item is current, when it was answered,
 * when silence has gone on too long. These types are what the page renders,
 * and the parsers below turn untrusted socket JSON into them. Every field is
 * read defensively: a malformed frame degrades to an empty value, it never
 * throws inside a live session.
 *
 * Contract: KB_LIVE_CONTRACT (owner-approved 2026-09-25), mirrored by
 * salesguru-services `websocket/kb_live/`.
 */

export type KbItemType = 'objection' | 'faq' | 'landmine' | 'competitor';
export type KbSpeaker = 'salesperson' | 'ai_customer';
export type ItemAnsweredStatus = 'answered' | 'no_answer';
export type SilenceStage = 'reask' | 'checking_in' | 'moved_on';

export interface SessionStartedData {
  sessionId: string;
  trackName?: string;
  totalItems: number;
}

export interface TranscriptDeltaData {
  speaker: KbSpeaker;
  text: string;
}

/** The customer has just raised one KB item. `text` is the line they spoke. */
export interface ItemRaisedData {
  trackItemId: string;
  itemType: string;
  /** 1-based position of this item in the track. */
  index: number;
  totalItems: number;
  text: string;
}

export interface QuestionDetectedData {
  question: string;
  category: string;
}

export interface StoredAnswerData {
  question: string;
  storedAnswer: string;
  category: string;
}

export interface LandmineCardData {
  trigger: string;
  /** The customer's words that raised the item; the alert quotes them. */
  matchedText: string;
  quickTips: string;
  canSay: string[];
  cannotSay: string[];
}

export interface CompetitorCardData {
  name: string;
  /** The customer's words that raised the item; the alert quotes them. */
  matchedText: string;
  advantages: string[];
  weaknesses: string[];
  talkingPoints: string;
}

/** One item closed. `itemsAnswered` counts `answered` only, server-side. */
export interface ItemAnsweredData {
  trackItemId: string;
  itemType: string;
  status: ItemAnsweredStatus;
  itemsAnswered: number;
  totalItems: number;
}

export interface SilenceData {
  stage: SilenceStage;
  trackItemId: string;
}

export interface SessionCompletedData {
  /** Items the rep answered. */
  itemsCompleted: number;
  /** Items the customer got to raise before the session ended. */
  itemsReached: number;
  totalItems: number;
}

type WireRecord = Record<string, unknown>;

const SPEAKERS: readonly KbSpeaker[] = ['salesperson', 'ai_customer'];
const SILENCE_STAGES: readonly SilenceStage[] = ['reask', 'checking_in', 'moved_on'];

const asRecord = (value: unknown): WireRecord =>
  value && typeof value === 'object' ? (value as WireRecord) : {};

export const toText = (value: unknown): string => (typeof value === 'string' ? value : '');

export const toCount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;

/**
 * Snapshot lists arrive either as plain strings or as `{point}` / `{text}`
 * objects (the live-call landmine shape). Both flatten to non-empty strings.
 */
export const toTextList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      const record = asRecord(entry);
      return toText(record?.point) || toText(record?.text);
    })
    .map((entry) => entry.trim())
    .filter(Boolean);
};

/** First non-empty string among several wire spellings of one field. */
const firstText = (record: WireRecord, keys: string[]): string =>
  keys.map((key) => toText(record?.[key])).find(Boolean) ?? '';

/** First array among several wire spellings of one field. */
const firstList = (record: WireRecord, keys: string[]): string[] =>
  keys.map((key) => toTextList(record?.[key])).find((list) => list.length > 0) ?? [];

export function parseSessionStarted(raw: unknown): SessionStartedData {
  const record = asRecord(raw);
  return {
    sessionId: toText(record?.sessionId),
    trackName: toText(record?.trackName) || undefined,
    totalItems: toCount(record?.totalItems),
  };
}

/** Null when the speaker is unknown: an unattributed caption is not shown. */
export function parseTranscriptDelta(raw: unknown): TranscriptDeltaData | null {
  const record = asRecord(raw);
  const speaker = SPEAKERS.find((candidate) => candidate === record?.speaker);
  const text = toText(record?.text);
  return speaker && text ? { speaker, text } : null;
}

/** Null without a track item id: there is nothing to attach a card to. */
export function parseItemRaised(raw: unknown): ItemRaisedData | null {
  const record = asRecord(raw);
  const trackItemId = toText(record?.trackItemId);
  if (!trackItemId) return null;
  return {
    trackItemId,
    itemType: toText(record?.itemType),
    index: toCount(record?.index),
    totalItems: toCount(record?.totalItems),
    text: toText(record?.text),
  };
}

export function parseQuestionDetected(raw: unknown): QuestionDetectedData {
  const record = asRecord(raw);
  return { question: toText(record?.question), category: toText(record?.category) };
}

export function parseStoredAnswer(raw: unknown): StoredAnswerData {
  const record = asRecord(raw);
  return {
    question: toText(record?.question),
    storedAnswer: firstText(record, ['stored_answer', 'storedAnswer']),
    category: toText(record?.category),
  };
}

export function parseLandmineCard(raw: unknown): LandmineCardData {
  const record = asRecord(raw);
  return {
    trigger: toText(record?.trigger),
    matchedText: firstText(record, ['matched_text', 'matchedText']),
    quickTips: firstText(record, ['quick_tips', 'quickTips']),
    canSay: firstList(record, ['can_say', 'canSay']),
    cannotSay: firstList(record, ['cannot_say', 'cannotSay']),
  };
}

export function parseCompetitorCard(raw: unknown): CompetitorCardData {
  const record = asRecord(raw);
  return {
    name: firstText(record, ['competitor', 'name', 'competitorName']),
    matchedText: firstText(record, ['matched_text', 'matchedText']),
    advantages: toTextList(record?.advantages),
    weaknesses: toTextList(record?.weaknesses),
    talkingPoints: firstText(record, ['talking_points', 'talkingPoints']),
  };
}

export function parseItemAnswered(raw: unknown): ItemAnsweredData | null {
  const record = asRecord(raw);
  const trackItemId = toText(record?.trackItemId);
  if (!trackItemId) return null;
  return {
    trackItemId,
    itemType: toText(record?.itemType),
    status: record?.status === 'answered' ? 'answered' : 'no_answer',
    itemsAnswered: toCount(record?.itemsAnswered),
    totalItems: toCount(record?.totalItems),
  };
}

export function parseSilence(raw: unknown): SilenceData | null {
  const record = asRecord(raw);
  const stage = SILENCE_STAGES.find((candidate) => candidate === record?.stage);
  return stage ? { stage, trackItemId: toText(record?.trackItemId) } : null;
}

export function parseSessionCompleted(raw: unknown): SessionCompletedData {
  const record = asRecord(raw);
  return {
    itemsCompleted: toCount(record?.itemsCompleted),
    itemsReached: toCount(record?.itemsReached),
    totalItems: toCount(record?.totalItems),
  };
}
